import { Controller } from "@hotwired/stimulus"
import Rails from "@rails/ujs"
import { Toast, Tooltip } from "bootstrap"
import Inputmask from "inputmask"
import L from "leaflet"

export default class extends Controller {
  static targets = ["map", "geojson", "warehouse", "status", "counter", "submit", "minWeight", "maxWeight"]
  static values = { defaultLatitude: Number, defaultLongitude: Number, readOnly: Boolean }

  connect() {
    this.points = this.readPoints()
    this.markers = []
    this.tooltips = Array.from(this.element.querySelectorAll('[data-bs-toggle="tooltip"]'))
      .map((element) => Tooltip.getOrCreateInstance(element))
    this.setupWeightMasks()
    this.map = L.map(this.mapTarget, { zoomControl: true }).setView(this.initialCenter(), 12)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap"
    }).addTo(this.map)

    this.polygon = L.polygon([], {
      color: "#dc3545",
      fillColor: "#dc3545",
      fillOpacity: 0.14,
      weight: 3
    }).addTo(this.map)

    if (!this.readOnlyValue) {
      this.map.on("click", (event) => this.addPoint(event.latlng))
      this.element.addEventListener("submit", this.validateBeforeSubmit)
    }
    this.render()

    if (this.points.length >= 3) {
      this.map.fitBounds(L.latLngBounds(this.points), { padding: [24, 24] })
    } else {
      this.centerOnWarehouse()
    }
  }

  disconnect() {
    this.element.removeEventListener("submit", this.validateBeforeSubmit)
    this.tooltips?.forEach((tooltip) => tooltip.dispose())
    this.weightInputs?.forEach((input) => input.inputmask?.remove())
    this.map?.remove()
  }

  validateBeforeSubmit = (event) => {
    if (this.points.length >= 3) return

    event.preventDefault()
    const message = "Para salvar a restrição, desenhe uma área com pelo menos três pontos no mapa."
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("text-danger")
    this.mapTarget.classList.add("border", "border-danger")
    this.showValidationToast(message)
    this.mapTarget.scrollIntoView({ behavior: "smooth", block: "center" })
    this.enableSubmitButton()
  }

  centerOnWarehouse() {
    const warehouseCoordinates = this.selectedWarehouseCoordinates()
    const coordinates = warehouseCoordinates || this.defaultWarehouseCoordinates()

    if (coordinates) this.map.setView(coordinates, warehouseCoordinates ? 13 : 12)
  }

  initialCenter() {
    return this.selectedWarehouseCoordinates() ||
      this.defaultWarehouseCoordinates() ||
      [-23.5505, -46.6333]
  }

  selectedWarehouseCoordinates() {
    const option = this.warehouseTarget.selectedOptions[0]
    return this.validCoordinates(option?.dataset.latitude, option?.dataset.longitude)
  }

  defaultWarehouseCoordinates() {
    if (!this.hasDefaultLatitudeValue || !this.hasDefaultLongitudeValue) return null

    return this.validCoordinates(this.defaultLatitudeValue, this.defaultLongitudeValue)
  }

  validCoordinates(latitudeValue, longitudeValue) {
    const latitude = Number(latitudeValue)
    const longitude = Number(longitudeValue)

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude === 0 || longitude === 0) return null

    return [latitude, longitude]
  }

  undo() {
    if (this.readOnlyValue) return

    this.points.pop()
    this.render()
  }

  clear() {
    if (this.readOnlyValue) return

    this.points = []
    this.render()
  }

  addPoint(latlng) {
    if (this.readOnlyValue) return

    this.points.push([latlng.lat, latlng.lng])
    this.render()
  }

  render() {
    this.markers.forEach((marker) => marker.remove())
    this.markers = this.readOnlyValue ? [] : this.points.map((point, index) => this.createVertex(point, index))
    this.polygon.setLatLngs(this.points)
    if (!this.readOnlyValue) this.syncGeojson()

    const count = this.points.length
    this.counterTarget.textContent = `${count} ${count === 1 ? "ponto" : "pontos"}`
    this.statusTarget.classList.remove("text-danger", "text-success")

    if (this.readOnlyValue) {
      this.statusTarget.textContent = count >= 3 ? "Área cadastrada na regra." : "Esta regra não possui uma área válida."
      return
    }

    if (count >= 3) {
      this.mapTarget.classList.remove("border", "border-danger")
      this.statusTarget.textContent = "Área pronta. Você ainda pode arrastar os pontos para ajustar."
      this.statusTarget.classList.add("text-success")
    } else {
      this.statusTarget.textContent = `Adicione mais ${3 - count} ${3 - count === 1 ? "ponto" : "pontos"}.`
    }
  }

  createVertex(point, index) {
    const icon = L.divIcon({
      className: "restriction-map-vertex",
      html: "<span></span>",
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    })
    const marker = L.marker(point, { draggable: true, icon }).addTo(this.map)

    marker.on("drag", (event) => {
      const position = event.target.getLatLng()
      this.points[index] = [position.lat, position.lng]
      this.polygon.setLatLngs(this.points)
      this.syncGeojson()
    })

    marker.on("click", (event) => L.DomEvent.stopPropagation(event))
    return marker
  }

  syncGeojson() {
    if (this.points.length < 3) {
      this.geojsonTarget.value = ""
      return
    }

    const coordinates = this.points.map(([latitude, longitude]) => [longitude, latitude])
    coordinates.push([...coordinates[0]])
    this.geojsonTarget.value = JSON.stringify({ type: "Polygon", coordinates: [coordinates] })
  }

  readPoints() {
    try {
      const geometry = JSON.parse(this.geojsonTarget.value || "{}")
      const ring = geometry.type === "MultiPolygon" ? geometry.coordinates?.[0]?.[0] : geometry.coordinates?.[0]
      if (!Array.isArray(ring)) return []

      const coordinates = ring.map(([longitude, latitude]) => [Number(latitude), Number(longitude)])
      if (coordinates.length > 1 && this.samePoint(coordinates[0], coordinates[coordinates.length - 1])) coordinates.pop()
      return coordinates.filter(([latitude, longitude]) => Number.isFinite(latitude) && Number.isFinite(longitude))
    } catch (_error) {
      return []
    }
  }

  samePoint(first, second) {
    return first[0] === second[0] && first[1] === second[1]
  }

  showValidationToast(message) {
    const host = document.getElementById("flash-messages") || document.body
    let container = host.querySelector("[data-restriction-toast-container]")

    if (!container) {
      container = document.createElement("div")
      container.className = "toast-container position-fixed top-0 end-0 p-3"
      container.style.zIndex = "1080"
      container.dataset.restrictionToastContainer = "true"
      host.appendChild(container)
    }

    const toastElement = document.createElement("div")
    toastElement.className = "toast align-items-center text-bg-danger border-0"
    toastElement.setAttribute("role", "alert")
    toastElement.setAttribute("aria-live", "assertive")
    toastElement.setAttribute("aria-atomic", "true")

    const content = document.createElement("div")
    content.className = "d-flex"

    const body = document.createElement("div")
    body.className = "toast-body"
    body.textContent = message

    const closeButton = document.createElement("button")
    closeButton.type = "button"
    closeButton.className = "btn-close btn-close-white me-2 m-auto"
    closeButton.dataset.bsDismiss = "toast"
    closeButton.setAttribute("aria-label", "Fechar")

    content.append(body, closeButton)
    toastElement.appendChild(content)
    container.appendChild(toastElement)
    toastElement.addEventListener("hidden.bs.toast", () => toastElement.remove(), { once: true })
    Toast.getOrCreateInstance(toastElement, { delay: 4000 }).show()
  }

  enableSubmitButton() {
    setTimeout(() => Rails.enableElement(this.element), 25)
  }

  setupWeightMasks() {
    this.weightInputs = [this.minWeightTarget, this.maxWeightTarget]
    this.weightInputs.forEach((input) => {
      Inputmask({
        alias: "numeric",
        groupSeparator: ".",
        radixPoint: ",",
        digits: 2,
        digitsOptional: true,
        autoGroup: true,
        allowMinus: false,
        rightAlign: false,
        removeMaskOnSubmit: true,
        placeholder: ""
      }).mask(input)
    })
  }
}
